import { useCallback, useState } from "react";
import { message } from "antd";
import { Node, NodeProps, NodeToolbar, Position, useReactFlow } from "reactflow";
import GPTInput from "../gpt-input/gpt-input";
import { ReactComponent as BrainstormIcon } from '../../../assets/node-icons/brainstorm.svg';
import { ReactComponent as ExploreIcon } from '../../../assets/node-icons/search.svg';
import { ReactComponent as HierarchyIcon } from '../../../assets/node-icons/hierarchy.svg';
import { ReactComponent as SendIcon } from '../../../assets/node-icons/send.svg';
import { ReactComponent as MemoIcon } from '../../../assets/node-icons/memo.svg';
import { ReactComponent as TopicIcon } from '../../../assets/node-icons/topic.svg';

import './expand-toolbar.scss'
import { ChatNodeData } from "../../nodes/response-node/response-node.model";
import { ResponseState } from "../input.model";
import { createBrainstormNode, createChatNode } from "../../nodes/response-node/response-node.helper";
import { BrainstormNodeData } from "../../nodes/brainstorm-node/brainstorm-node.model";
import { extractNodeTexts, predictSubtopics } from "../../nodes/node.helper";
import { InputHoverState } from "../../nodes/node.model";
import { getStoredApiKey } from "../ApiKeyModal";

const ExpandToolbar = (props: any) => {
  const defaultPlaceholder = 'Follow up';
  const [placeholder, setPlaceholder] = useState(defaultPlaceholder)
  const [responseInputState, setResponseInputState] = useState<ResponseState>(ResponseState.INPUT);
  const [input, setInput] = useState('');

  const reactFlowInstance = useReactFlow();

  const addInstantChatNode = useCallback(
    (input: string) => {
      const data: ChatNodeData = {
        chatHistory: [
          ...reactFlowInstance.getNode(props.sourceId)?.data.chatHistory ?? [],
          {
            role: 'system',
            content: `Conversation is now focused on these topics: ${props.topic}:`
          }
        ],
        // We want chat node to already show a response
        placeholder: '',
        state: {
          input,
          responseInputState: ResponseState.LOADING
        }
      };
      createChatNode(reactFlowInstance, props.sourceId, data);
    },
    [reactFlowInstance]
  );

  const addBrainstormNode = useCallback(
    (topic: string) => {

      const data: BrainstormNodeData = {
        placeholder: topic, // If no response yet, use placeholder
        state: {
          input: topic,
          // questions?: {};
          responseInputState: ResponseState.LOADING,
        }
      }
      createBrainstormNode(reactFlowInstance, props.sourceId, data);
    },
    [reactFlowInstance]
  );

  /**
   * Creates another chat node as a response below the group node
   *
   * @param prompt 
   * @returns 
   */
  const generateResponse = async (prompt: string) => {
    if (!prompt) {
      return;
    }
    if (!getStoredApiKey()) {
      message.warning('API key is missing. You can set it in the Help menu.');
      return;
    }
    addInstantChatNode(prompt);
    props.setInputState(InputHoverState.OUT);
    // props.setToolbarAvailable(false);
  }

  /**
   * Create a brainstorm node to generate questions
   */
  const generateQuestions = async (prompt: string) => {
    if (!prompt) {
      return;
    }
    if (!getStoredApiKey()) {
      message.warning('API key is missing. You can set it in the Help menu.');
      return;
    }
    addBrainstormNode(prompt);
    props.setInputState(InputHoverState.OUT);
    // props.setToolbarAvailable(false);
  }

  /**
   * Generates subtopics around the selection of nodes
   */
  const generateSubtopics = useCallback(
    async (selectedNodeIds: string[]) => {
      if (!getStoredApiKey()) {
        message.warning('API key is missing. You can set it in the Help menu.');
        return;
      }
      setResponseInputState(ResponseState.LOADING);
      setInput(`Generating subtopics from ${props.topic}`);
      await predictSubtopics(props.topic.split(',').map((text: string) => text.trim()), selectedNodeIds, reactFlowInstance);
      setInput('');
      setResponseInputState(ResponseState.INPUT);
    },
    [reactFlowInstance]
  );

  return (
    <div className="expand-toolbar">
      <div className="expand-toolbar-box">
        <div className="default-buttons">
          <button
            onClick={() => generateResponse(props.topic)}
            onMouseEnter={() => setPlaceholder(props.topic)}
            onMouseLeave={() => setPlaceholder(defaultPlaceholder)}
          >
            Prompt <SendIcon/>
          </button>
          <button
            onClick={() => generateResponse(`Tell me about ${props.topic}`)}
            onMouseEnter={() => setPlaceholder(`Tell me about ${props.topic}`)}
            onMouseLeave={() => setPlaceholder(defaultPlaceholder)}
          >
            Explain <ExploreIcon/>
          </button>
          <button
            onClick={() => generateQuestions(`${props.topic}`)}
            onMouseEnter={() => setPlaceholder(`Generate questions related to ${props.topic}`)}
            onMouseLeave={() => setPlaceholder(defaultPlaceholder)}
          >
            Questions <BrainstormIcon/>
          </button>
          <button
            onClick={() => generateSubtopics([props.sourceId])}
            onMouseEnter={() => setPlaceholder(`Recommend subtopics of ${props.topic} around this node`)}
            onMouseLeave={() => setPlaceholder(defaultPlaceholder)}
          >
            Subtopics <HierarchyIcon/>
          </button>
        </div>
        <GPTInput
          sourceId={props.sourceId}
          responseState={responseInputState}
          setResponseState={() => {}}
          generateResponse={(response: string) => generateResponse(response)}
          placeholder={placeholder}
          input={input}
          setInput={setInput}
          setInputState={props.setInputState}
        />
      </div>
    </div>
  )
}

export default ExpandToolbar;